import { useEffect, useRef, useState, type MutableRefObject, type ReactNode } from 'react';
import { Pencil, X } from 'lucide-react';
import { cn, getInitials } from '@/lib/utils';
import { inputLocalAIso, isoAInputLocal } from '@/lib/tareas';
import { PRIORIDADES, type ActualizarTareaInput, type PrioridadTarea, type TableroColumna, type TareaDetalle } from '@/types/tareas';
import { AsignadosPicker, EtiquetasPicker } from '@/components/tareas/Pickers';
import { EtiquetaChip, PrioridadBadge, VencimientoTexto } from '@/components/tareas/TareaPiezas';

const campo = 'w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white';
const etiquetaCampo = 'block text-xs font-semibold uppercase tracking-wide text-text-secondary mb-1';
const COLOR_COLUMNA_DEFAULT = '#94a3b8';

type CampoId = 'estado' | 'prioridad' | 'vencimiento' | 'consultorio' | 'asignados' | 'etiquetas' | 'descripcion';

interface Props {
  tarea: TareaDetalle;
  columnas: TableroColumna[];
  consultorios: { id: string; nombre: string }[];
  alGuardar: (cambios: ActualizarTareaInput) => void;
  alMover: (columnaId: string) => void;
  /** Se marca mientras hay un campo en edición, para que Esc cancele la edición en vez de cerrar el modal. */
  editandoRef: MutableRefObject<boolean>;
}

/**
 * Un campo se ve como texto de solo lectura; al hacer clic (o con Enter/Espacio) pasa a edición solo ese
 * campo. Se guarda al elegir un valor o al salir del campo, y Esc cancela sin guardar.
 */
function CampoEditable({
  etiqueta, editando, onEditar, lectura, children,
}: { etiqueta: string; editando: boolean; onEditar: () => void; lectura: ReactNode; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <span className={etiquetaCampo}>{etiqueta}</span>
      {editando ? children : (
        <button
          type="button"
          onClick={onEditar}
          aria-label={`Editar ${etiqueta.toLowerCase()}`}
          className="group w-full min-h-[2.25rem] flex items-start justify-between gap-2 text-left text-sm text-text-primary rounded-lg px-2.5 py-2 border border-transparent hover:border-gray-200 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
        >
          <span className="min-w-0 flex-1 break-words">{lectura}</span>
          <Pencil size={12} className="mt-1 flex-shrink-0 text-gray-400 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100" aria-hidden />
        </button>
      )}
    </div>
  );
}

const Vacio = ({ children }: { children: ReactNode }) => <span className="text-gray-400">{children}</span>;

export function DatosTarea({ tarea, columnas, consultorios, alGuardar, alMover, editandoRef }: Props) {
  const [editando, setEditando] = useState<CampoId | null>(null);
  const [vencimiento, setVencimiento] = useState(isoAInputLocal(tarea.fechaVencimiento));
  const [descripcion, setDescripcion] = useState(tarea.descripcion ?? '');
  // Esc cancela: evita que el blur que sigue al desmontar el campo guarde lo que se descartó.
  const cancelando = useRef(false);

  const cerrar = () => setEditando(null);
  const editar = (c: CampoId) => { cancelando.current = false; setEditando(c); };

  useEffect(() => {
    editandoRef.current = editando !== null;
    return () => { editandoRef.current = false; };
  }, [editando, editandoRef]);

  // Re-sincroniza cuando el servidor devuelve datos más nuevos, sin pisar el campo que se está escribiendo.
  useEffect(() => {
    const enfocado = document.activeElement?.getAttribute('data-campo');
    if (enfocado !== 'descripcion') setDescripcion(tarea.descripcion ?? '');
    if (enfocado !== 'vencimiento') setVencimiento(isoAInputLocal(tarea.fechaVencimiento));
  }, [tarea.updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const cancelarConEsc = (e: React.KeyboardEvent, restaurar?: () => void) => {
    if (e.key !== 'Escape') return;
    cancelando.current = true;
    restaurar?.();
    cerrar();
  };

  const columnaActual = columnas.find(c => c.id === tarea.columnaId);
  const consultorio = consultorios.find(c => c.id === tarea.consultorioId);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-x-4 gap-y-3">
        <CampoEditable
          etiqueta="Estado"
          editando={editando === 'estado'}
          onEditar={() => editar('estado')}
          lectura={(
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: columnaActual?.color ?? COLOR_COLUMNA_DEFAULT }} />
              {columnaActual?.nombre ?? tarea.columna?.nombre ?? '—'}
            </span>
          )}
        >
          <select
            autoFocus
            value={tarea.columnaId}
            onChange={e => { alMover(e.target.value); cerrar(); }}
            onBlur={cerrar}
            onKeyDown={e => cancelarConEsc(e)}
            className={campo}
          >
            {columnas.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select>
        </CampoEditable>

        <CampoEditable
          etiqueta="Prioridad"
          editando={editando === 'prioridad'}
          onEditar={() => editar('prioridad')}
          lectura={<PrioridadBadge prioridad={tarea.prioridad} />}
        >
          <select
            autoFocus
            value={tarea.prioridad}
            onChange={e => { alGuardar({ prioridad: e.target.value as PrioridadTarea }); cerrar(); }}
            onBlur={cerrar}
            onKeyDown={e => cancelarConEsc(e)}
            className={campo}
          >
            {PRIORIDADES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </CampoEditable>

        <CampoEditable
          etiqueta="Vencimiento"
          editando={editando === 'vencimiento'}
          onEditar={() => editar('vencimiento')}
          lectura={tarea.fechaVencimiento ? <VencimientoTexto tarea={tarea} className="text-sm" /> : <Vacio>Sin fecha</Vacio>}
        >
          <div className="flex gap-1">
            <input
              autoFocus
              type="datetime-local"
              data-campo="vencimiento"
              value={vencimiento}
              onChange={e => setVencimiento(e.target.value)}
              onBlur={() => {
                if (!cancelando.current && vencimiento !== isoAInputLocal(tarea.fechaVencimiento)) {
                  alGuardar({ fechaVencimiento: inputLocalAIso(vencimiento) });
                }
                cerrar();
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') e.currentTarget.blur();
                cancelarConEsc(e, () => setVencimiento(isoAInputLocal(tarea.fechaVencimiento)));
              }}
              className={campo}
            />
            {vencimiento && (
              <button
                type="button"
                // onMouseDown evita que el input pierda el foco (y guarde) antes de procesar el clic.
                onMouseDown={e => e.preventDefault()}
                onClick={() => { cancelando.current = true; setVencimiento(''); alGuardar({ fechaVencimiento: null }); cerrar(); }}
                className="px-2 text-gray-400 hover:text-red-500"
                aria-label="Quitar vencimiento"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </CampoEditable>

        <CampoEditable
          etiqueta="Consultorio"
          editando={editando === 'consultorio'}
          onEditar={() => editar('consultorio')}
          lectura={consultorio ? consultorio.nombre : <Vacio>Sin consultorio</Vacio>}
        >
          <select
            autoFocus
            value={tarea.consultorioId ?? ''}
            onChange={e => { alGuardar({ consultorioId: e.target.value || null }); cerrar(); }}
            onBlur={cerrar}
            onKeyDown={e => cancelarConEsc(e)}
            className={campo}
          >
            <option value="">Sin consultorio</option>
            {consultorios.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select>
        </CampoEditable>
      </div>

      <CampoEditable
        etiqueta="Asignados"
        editando={editando === 'asignados'}
        onEditar={() => editar('asignados')}
        lectura={tarea.asignados.length ? (
          <span className="flex flex-wrap gap-1.5">
            {tarea.asignados.map(a => (
              <span key={a.id} className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 pl-0.5 pr-2 py-0.5 text-xs">
                <span className="w-5 h-5 rounded-full bg-primary-100 text-primary-700 text-[9px] font-semibold flex items-center justify-center overflow-hidden">
                  {a.fotoUrl ? <img src={a.fotoUrl} alt="" className="w-full h-full object-cover" /> : getInitials(a.nombre)}
                </span>
                {a.nombre}
              </span>
            ))}
          </span>
        ) : <Vacio>Sin asignar</Vacio>}
      >
        <AsignadosPicker
          value={tarea.asignados.map(a => a.id)}
          onChange={ids => alGuardar({ asignadoIds: ids })}
          open
          onOpenChange={abierto => { if (!abierto) cerrar(); }}
        />
      </CampoEditable>

      <CampoEditable
        etiqueta="Etiquetas"
        editando={editando === 'etiquetas'}
        onEditar={() => editar('etiquetas')}
        lectura={tarea.etiquetas.length
          ? <span className="flex flex-wrap gap-1">{tarea.etiquetas.map(e => <EtiquetaChip key={e.id} etiqueta={e} />)}</span>
          : <Vacio>Sin etiquetas</Vacio>}
      >
        <EtiquetasPicker
          value={tarea.etiquetas.map(e => e.id)}
          onChange={ids => alGuardar({ etiquetaIds: ids })}
          open
          onOpenChange={abierto => { if (!abierto) cerrar(); }}
        />
      </CampoEditable>

      <CampoEditable
        etiqueta="Descripción"
        editando={editando === 'descripcion'}
        onEditar={() => editar('descripcion')}
        lectura={tarea.descripcion
          ? <span className="block whitespace-pre-wrap">{tarea.descripcion}</span>
          : <Vacio>Sin descripción. Haz clic para agregar.</Vacio>}
      >
        <textarea
          autoFocus
          value={descripcion}
          onChange={e => setDescripcion(e.target.value)}
          onBlur={() => {
            if (!cancelando.current && descripcion !== (tarea.descripcion ?? '')) {
              alGuardar({ descripcion: descripcion.trim() ? descripcion : null });
            }
            cerrar();
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) e.currentTarget.blur();
            cancelarConEsc(e, () => setDescripcion(tarea.descripcion ?? ''));
          }}
          rows={8}
          data-campo="descripcion"
          placeholder="Detalles de la tarea. La descripción se almacena cifrada. Ctrl+Enter para guardar, Esc para cancelar."
          className={cn(campo, 'resize-y')}
        />
      </CampoEditable>
    </div>
  );
}
